import { AppData, DocumentServicesObject } from '@wix/document-services-types';

import { EditorPointerServiceAPI } from '@wix/editor-platform-feature-store';

import { getNativeElementsPanelAPI } from './nativeElementsPanelAPI';

import { getEditorSelectionHandlers } from './utils';

import { ElementsPanelService, GetElementsPanelAPI } from './types';
import { EMTPY_ELEMENTS_PANEL_DATA } from './constants';
import { PlatformApiMethods } from '@wix/editor-platform-host-integration-apis';
import {
  ManifestComponentExtensionType,
  ComponentsService,
} from '@wix/editor-platform-manifest-services';
import {
  getOOIWidgetElementsPanelProps,
  getBuilderComponentElementsPanelProps,
  getElementsPanelPropsForUnknownType,
} from './flow';

const resolversByType = {
  [ManifestComponentExtensionType.OOI]: getOOIWidgetElementsPanelProps,
  [ManifestComponentExtensionType.BUILDER]:
    getBuilderComponentElementsPanelProps,
  [ManifestComponentExtensionType.UNKNOWN]: getElementsPanelPropsForUnknownType,
};

export function initElementsPanelService(
  documentServicesAPI: DocumentServicesObject,
  platformApiMethods: PlatformApiMethods,
  componentsAPI: ComponentsService,
  editorPointerAPI?: EditorPointerServiceAPI,
): ElementsPanelService {
  const getElementsPanelAPI: GetElementsPanelAPI = (editorPointer, options) => {
    const manifestElementsPanelData =
      componentsAPI.getElementsData(editorPointer);

    const editorSelectionHandlers = getEditorSelectionHandlers(
      platformApiMethods,
      editorPointerAPI,
    );

    if (!editorPointerAPI) {
      return EMTPY_ELEMENTS_PANEL_DATA;
    }

    const extensionType =
      manifestElementsPanelData.extensionType as keyof typeof resolversByType;

    const resolver = resolversByType[extensionType];

    if (manifestElementsPanelData.showElementsPanel && resolver) {
      return resolver(manifestElementsPanelData as never, {
        documentServicesAPI,
        platformApiMethods,
        componentsAPI,
        editorPointer,
        editorSelectionHandlers,
        editorPointerAPI,
      });
    }

    if (!options) {
      return EMTPY_ELEMENTS_PANEL_DATA;
    }

    return getNativeElementsPanelAPI(
      documentServicesAPI,
      options,
      editorSelectionHandlers,
      editorPointerAPI,
    );
  };

  return {
    getElementsPanelAPI: (...args) => {
      const elementsPanelAPI = getElementsPanelAPI(...args);

      if (!elementsPanelAPI) {
        return null;
      }

      return {
        ...elementsPanelAPI,
        openHelpPanel: (options?: { helpId: string }) =>
          platformApiMethods.editor.openHelpPanel(
            {} as AppData,
            '',
            options ?? {},
          ),
      };
    },
  };
}
